import React, { Component } from 'react';

import PaginationComponent from './PaginationComponent';
import TableStateCard from './TableStateCard';
import { sortClass, nextSortDir, sortAriaSort } from '../utils/table';
import dateFormat from 'dateformat';
import { navigateTo } from '../utils/navigation';
import { buildPaginationQuery, getPaginatedData, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { hasActiveSearch, getSearchEmptyMessage } from '../utils/tableState';
import { getSelectedBranchId } from '../utils/branch';
import { isCafeteriaMode } from '../utils/appMode';

var config = process.env;

// En modo cafetería el listado mira el salón completo: cafetería + mesas (el cover de mesa lo
// genera el mismo salón). El backend acepta la lista separada por comas.
const CAFETERIA_CHANNEL_FILTER = 'cafeteria,mesas';

// Canal con el que abre la grilla en cada modo. En retail arranca acotada a retail: abrir en
// "Todos" mezcla los ítems de cafetería con los productos del mostrador, y ni Proveedor ni
// Stock actual significan lo mismo para unos que para otros.
function defaultChannel() {
    return isCafeteriaMode() ? CAFETERIA_CHANNEL_FILTER : 'retail';
}

class TableSoldProductsComponent extends Component {

    constructor() {
        super();
        this.state = {
            quantityShowRows: 25,
            currentPage: 1,
            totalItems: 0,
            search: '',
            searchInput: '',
            sortBy: 'date',
            sortDir: 'desc',
            channel: defaultChannel(),
            data: [],
            loaded: false,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    fetchData = ({
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        sortBy = this.state.sortBy,
        sortDir = this.state.sortDir,
        channel = this.state.channel,
        showLoader = true,
    } = {}) => {
        if (showLoader) {
            this.setState({ loaded: false });
        }
        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };
        // `branchId` va explícito, como en la grilla de Ventas: sin él el backend cae a la
        // sucursal del token, así que un admin con "Todas las sucursales" en el header veía sólo
        // la suya y sin ninguna señal de que había un recorte.
        const query = buildPaginationQuery({
            page, limit, search, sortBy, sortDir,
            extraParams: {
                branchId: getSelectedBranchId() || undefined,
                channel: channel || undefined,
            },
        });
        // Secuencia de pedido: la respuesta escribe el canal/orden CON EL QUE SE PIDIÓ, así que
        // una respuesta vieja que llega tarde pisaría la elección nueva y el combo se revertiría
        // solo (se lee como "el filtro no anda"). Sólo la última pedida puede tocar el estado.
        this.requestSeq = (this.requestSeq || 0) + 1;
        const seq = this.requestSeq;

        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}purchase/soldProductsByDate?${query}`, request)
        .then(results => results.json())
        .then(data => {
            if (seq !== this.requestSeq) { return; }
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
                // Sin esto la grilla queda en el loader para siempre, y como la barra de
                // controles no se renderiza mientras carga, el combo de canal se vuelve
                // inalcanzable: no hay forma de salir del canal que falló sin recargar.
                this.setState({ loaded: true });
            } else {
                const paginatedData = getPaginatedData(data);
                this.setState({
                    data: paginatedData.items || [],
                    quantityShowRows: paginatedData.limit || Number(limit),
                    currentPage: paginatedData.page || Number(page),
                    totalItems: paginatedData.total || 0,
                    search,
                    sortBy,
                    sortDir,
                    channel,
                    loaded: true
                });
            }
        })
        .catch(() => {
            if (seq !== this.requestSeq) { return; }
            this.setState({ loaded: true });
        });
    }

    drawRow = (item, index) => {
        return (
            <tr role="row" className="odd" key={item.purchaseId + item.productName} style={{ color: item.currentStock == 0 ? '#000' : '#5a6169', background: item.currentStock == 0 ? '#ffb8b8' : (index % 2 == 0) ? '#ffffff;' : '#fcfcfc' }}>
                <td>{item.purchaseId}</td>
                <td>{dateFormat(item.date, 'dS mmmm, yyyy - HH:MM')}</td>
                <td>{item.productName}</td>
                <td>{item.provider}</td>
                <td>{item.quantity}</td>
                <td>$ {item.price}</td>
                <td>{item.discount} %</td>
                <td>{item.currentStock}</td>
                {this.props.role == 1 ? (
                    <td>
                        <div className="btn-group btn-group-sm" role="group" aria-label="Table row actions">
                            <button
                                onClick={() => this.onViewAnalytics(item.code)}
                                type="button"
                                className="btn btn-white">
                                <i className="material-icons">insert_chart_outlined</i>
                            </button>
                        </div>
                    </td>
                ) : null}
            </tr>
        );
    }

    onViewAnalytics = (code) => {
        navigateTo(`/productAnalytics?code=${code}&source=soldProducts`);
    }

    loadData = () => {
        if (!this.state.loaded) {
            return <tbody></tbody>;
        }

        const items = this.state.data.map((item, index) => this.drawRow(item, index));
        // Acá sólo se cae con una búsqueda activa: la lista vacía SIN búsqueda la atiende el
        // empty state de `render`, que sabe distinguir "no hay nada" de "no hay nada en este canal".
        if (items.length === 0) {
            items.push(
                <tr key="empty"><td colSpan="9" style={{ textAlign: 'center', padding: '10px', border: '1px solid rgb(225, 229, 235)' }}>
                    {getSearchEmptyMessage(this.state.searchInput || this.state.search)}
                </td></tr>
            );
        }
        return <tbody>{items}</tbody>;
    }

    onChangeShowRows = () => {
        const itemSelected = Number(document.getElementById('select_rows').value);
        this.fetchData({ page: 1, limit: itemSelected });
    }

    onChangeChannel = (value) => {
        this.setState({ channel: value });
        this.fetchData({ page: 1, channel: value });
    }

    // En cafetería el combo acota dentro del salón; en retail es el filtro global de canales.
    renderChannelOptions = () => {
        if (isCafeteriaMode()) {
            return (
                <React.Fragment>
                    <option value={CAFETERIA_CHANNEL_FILTER}>Cafetería y mesas</option>
                    <option value="cafeteria">Solo cafetería</option>
                    <option value="mesas">Solo mesas</option>
                </React.Fragment>
            );
        }
        return (
            <React.Fragment>
                {/* Sin "Eventos" a propósito: una venta de evento genera purchase + ticket +
                    event_admission, nunca una línea de product_purchase (EventController), así
                    que la opción sólo podría devolver la lista vacía. */}
                <option value="">Todos</option>
                <option value="retail">Retail</option>
                <option value="cafeteria">Cafetería</option>
                <option value="mesas">Mesas</option>
            </React.Fragment>
        );
    }

    // Barra de controles (cantidad de filas, canal, buscador). Va tanto sobre la tabla como
    // sobre el empty state: como el canal por defecto ya no es "Todos", un local que sólo
    // vende por cafetería cae en el empty state, y sin el selector a la vista quedaría
    // encerrado sin forma de llegar a sus ventas.
    renderToolbar = () => {
        return (
            <React.Fragment>
                <div className="dataTables_length" id="DataTables_Table_0_length">
                    <label>Show
                    <select id="select_rows" name="DataTables_Table_0_length" aria-controls="DataTables_Table_0" value={this.state.quantityShowRows} onChange={() => this.onChangeShowRows()}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    entries</label>
                    <label style={{ marginLeft: '1.5rem' }}>Canal&nbsp;
                    <select value={this.state.channel} onChange={(e) => this.onChangeChannel(e.target.value)}>
                        {this.renderChannelOptions()}
                    </select>
                    </label>
                </div>
                <div id="DataTables_Table_0_filter" className="dataTables_filter">
                    <label>Search:
                    <input id="input_search" type="search" className="" placeholder="" aria-controls="DataTables_Table_0" value={this.state.searchInput} onChange={(e) => this.onChangeInput(e.target.value)}/>
                    </label>
                </div>
            </React.Fragment>
        );
    }

    onChangeInput = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchData({ page: 1, search, showLoader: false });
        });
    }

    orderData = (column) => {
        this.fetchData({
            page: 1,
            sortBy: column,
            sortDir: nextSortDir(column, this.state.sortBy, this.state.sortDir),
        });
    }

    // Props de una cabecera ordenable: la clase y el aria-sort se derivan del state, así que
    // sobreviven al remonte que provoca el loader.
    sortableHeader = (column) => ({
        onClick: () => this.orderData(column),
        className: sortClass(column, this.state.sortBy, this.state.sortDir),
        'aria-sort': sortAriaSort(column, this.state.sortBy, this.state.sortDir),
        tabIndex: '0',
    })

    render() {
        const searchActive = hasActiveSearch(this.state.searchInput, this.state.search);

        if (!this.state.loaded) {
            return <TableStateCard loading />;
        }

        // Lista vacía sin búsqueda activa. El texto NO puede ser el mismo en los dos casos:
        // como la grilla abre filtrada por canal, "todavía no hay productos vendidos" sería
        // mentira para un local que sólo vende por cafetería (tiene cientos, pero no de este
        // canal). Sólo con "Todos" la lista vacía significa que de verdad no hay nada.
        if (this.state.totalItems === 0 && !searchActive) {
            const sinFiltroDeCanal = !this.state.channel;
            return (
                <div className="dataTables_wrapper no-footer">
                    {this.renderToolbar()}
                    {/* clear: los controles de la barra flotan (left/right), y sin esto la
                        tarjeta del empty state se acomoda al lado en vez de ocupar el ancho. */}
                    <div style={{ clear: 'both' }}>
                        <TableStateCard
                            icon="shopping_bag"
                            title={sinFiltroDeCanal
                                ? 'Todavia no hay productos vendidos para mostrar'
                                : 'No hay productos vendidos en este canal'}
                            message={sinFiltroDeCanal
                                ? 'Cuando existan ventas registradas, aca vas a ver el detalle de productos vendidos, cantidad y stock actual.'
                                : 'Probá cambiando el canal en el filtro de arriba.'}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                {this.renderToolbar()}

                <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info">
                    <thead>
                        <tr role="row">
                            <th {...this.sortableHeader('purchaseId')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '5%' }}>Venta</th>
                            <th {...this.sortableHeader('date')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '15%' }}>Fecha</th>
                            <th {...this.sortableHeader('productName')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '25%' }}>Producto</th>
                            <th {...this.sortableHeader('provider')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '10%' }}>Proveedor</th>
                            <th {...this.sortableHeader('quantity')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '5%' }}>Cantidad</th>
                            <th {...this.sortableHeader('price')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '10%' }}>Precio</th>
                            <th {...this.sortableHeader('discount')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '10%' }}>Descuento</th>
                            <th {...this.sortableHeader('currentStock')} aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1" style={{ width: '5%' }}>Stock actual</th>
                            {this.props.role == 1 ? (
                                <th tabIndex="0" aria-controls="DataTables_Table_0" rowSpan="1" colSpan="1">Acciones</th>
                            ) : null}
                        </tr>
                    </thead>
                    {this.loadData()}
                </table>

                <PaginationComponent
                    totalItems={this.state.totalItems}
                    quantityRows={this.state.quantityShowRows}
                    currentPage={this.state.currentPage}
                    onPageChange={(page) => this.fetchData({ page })}
                />
            </div>
        );
    }
}

export default TableSoldProductsComponent;
